"use client"

import { useEffect, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import type { AuthChangeEvent, Session } from "@supabase/supabase-js"
import { createClient } from "@/lib/supabase/client"
import { stripLocalePrefix } from "@/i18n/routing"
import { AuthModal } from "@/components/auth/auth-modal"

// Site-wide signup wall. Logged-out visitors get the branded black AuthModal
// forced open over an opaque backdrop on every page EXCEPT the allowlist below.
// This drives conversion without a hard redirect, so marketing HTML still
// renders for crawlers (SEO-safe) and the /dashboard middleware redirect stays
// as the real server-side guard for sensitive routes.

// Reachable without an account: the homepage, open marketing pages, the
// public booking funnel (/book, /shop), post-checkout pages (/payment),
// auth + legal, and design-QA (/preview). Everything else is walled.
//
// Keep this list in sync with lib/supabase/middleware.ts publicRoutes —
// middleware only guards /dashboard, but this wall is what logged-out users
// actually hit on marketing and booking surfaces in production.
const PUBLIC_EXACT: ReadonlyArray<string> = ["/"]
// "/book" = the public client booking funnel (browse + book before any login).
// "/clients" = the client entry door (sign-in / create-account for consumers) —
// must stay public, else the wall drops the PRO signup over it.
const PUBLIC_PREFIXES: ReadonlyArray<string> = [
  "/auth",
  "/invite",
  "/privacy",
  "/terms",
  "/refunds",
  "/book",
  "/shop",
  "/payment",
  "/clients",
  // Open marketing surfaces — reachable logged-out by design.
  "/pricing",
  "/press",
  "/blog",
  // The posts themselves. /blog is only the index — every card on it links to
  // /articles/[slug], so listing /blog alone opened the shelf and walled the
  // books: the list rendered, then the signup modal dropped the moment anyone
  // clicked through. middleware.ts has always treated /articles as public;
  // this is the client-side wall catching up to it.
  "/articles",
  "/help",
  "/listings",
  // The search page is the front door for barbers — it has its own
  // "Create free account" close at the foot of the results. Walling it
  // dropped the modal before anyone had seen a single shop.
  "/search",
  "/barbers",
  "/hiring",
  "/rewards",
  "/compare",
  // City answer pages (barber booth rental in <city>) and their index.
  "/booth-rental",
  "/about",
  "/contact",
  "/traction",
  "/changelog",
  // Status must stay reachable during an outage — walling it defeats the point.
  "/status",
  // Design-QA surfaces. Unlisted and noindex'd, but the wall would drop the
  // signup modal over them on a deployed preview, which makes them useless
  // for reviewing a design.
  "/preview",
]

function isPublicPath(rawPathname: string | null): boolean {
  if (!rawPathname) return true
  // Locale-agnostic: the Spanish landing /es must stay open exactly like /.
  const pathname = stripLocalePrefix(rawPathname)
  if (PUBLIC_EXACT.includes(pathname)) return true
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

// Never wall local review — `npm run dev` runs as NODE_ENV=development, and the
// hostname check also covers a local production build. Mirrors the dev bypass
// in lib/supabase/middleware.ts so localhost behaves identically here.
function isLocalEnv(): boolean {
  if (process.env.NODE_ENV !== "production") return true
  if (typeof window === "undefined") return false
  const h = window.location.hostname
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".local")
}

export function SignupWall() {
  const pathname = usePathname()
  const router = useRouter()
  const [authResolved, setAuthResolved] = useState(false)
  const [hasSession, setHasSession] = useState(false)

  // Read the session straight from storage (instant, no network) so the wall
  // appears without the flash/delay of the provider's retry-backoff, and flip
  // it the moment auth state changes (sign in inside the modal -> wall lifts).
  useEffect(() => {
    let active = true
    const supabase = createClient()

    supabase.auth.getSession().then((res: { data: { session: Session | null } }) => {
      if (!active) return
      setHasSession(!!res.data.session)
      setAuthResolved(true)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      if (!active) return
      setHasSession(!!session)
      setAuthResolved(true)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [])

  const gated = authResolved && !hasSession && !isLocalEnv() && !isPublicPath(pathname)

  if (!gated) return null

  // No separate opaque wall anymore — the AuthModal's own frosted overlay
  // (bg-black/40 + backdrop-blur) sits above the whole page (navbar included,
  // since it's portaled above the app root) and blurs it, so the gated page
  // teases through behind the signup instead of a flat black wall.
  //
  // The branded black modal. Only the explicit X closes it (dismissible=false)
  // — a stray click on the backdrop or Escape must NOT bounce the visitor to
  // the landing page. Closing via X routes home (the one open page). Signing in
  // flips hasSession via onAuthStateChange and unmounts the wall.
  return (
    <AuthModal
      open
      defaultMode="signup"
      dismissible={false}
      onOpenChange={(v) => {
        if (!v) router.push("/")
      }}
    />
  )
}
